/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Container, Icon } from 'semantic-ui-react';
import { withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

import { isStoreError, isStoreLoading, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

class RolesList extends React.Component {
  getStore() {
    return this.props.userRolesStore;
  }

  render() {
    const store = this.getStore();
    let content;
    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} />;
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder segmentCount={2} />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {this.renderHeader()}
        {content}
      </Container>
    );
  }

  renderHeader() {
    const store = this.getStore();
    const count = isStoreReady(store) ? store.list.length : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: 600, color: 'var(--rmit-blue)' }}>
          <Icon name="id badge" style={{ ...iconStyle, marginRight: '8px' }} />
          User Roles
        </h3>
        {count !== null && (
          <span className="workspace-status-badge" style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75em' }}>
            {count} total
          </span>
        )}
      </div>
    );
  }

  renderMain() {
    const roles = this.getStore().list;

    if (roles.length === 0) {
      return (
        <div className="workspace-empty-state" style={{ marginTop: '24px' }}>
          <div className="workspace-empty-state-icon"><Icon name="id badge" /></div>
          <div className="workspace-empty-state-title">No roles defined</div>
        </div>
      );
    }

    return (
      <table className="config-table">
        <thead>
          <tr>
            <th>Role Name</th>
            <th>Description</th>
            <th>User Type</th>
          </tr>
        </thead>
        <tbody>
          {roles.map(role => (
            <tr key={role.id}>
              <td><span className="config-name">{role.id}</span></td>
              <td style={{ fontSize: '0.85em', color: '#374151' }}>
                {role.description || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>}
              </td>
              <td>
                <span className="workspace-status-badge" style={{ background: '#f0f0ff', color: 'var(--rmit-blue)', border: '1px solid var(--rmit-blue-30)' }}>
                  {role.userType || 'N/A'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

export default inject('userRolesStore')(withRouter(observer(RolesList)));
